import { Link } from "react-router-dom";
import { FaFacebook, FaInstagram, FaYoutube, FaTiktok } from "react-icons/fa";
import { useState } from "react";
import profaLogo from "../../src/assets/logo-3.webp";
import { motion } from "framer-motion";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);

  const handleDisclaimerToggle = () => {
    setIsDisclaimerOpen(!isDisclaimerOpen);
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white relative">
      {/* Top Wave Decoration */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="w-full overflow-hidden"
      >
        <svg
          className="w-full h-12 transform rotate-180"
          viewBox="0 0 1440 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 50L48 45.8333C96 41.6667 192 33.3333 288 29.1667C384 25 480 25 576 33.3333C672 41.6667 768 58.3333 864 62.5C960 66.6667 1056 58.3333 1152 50C1248 41.6667 1344 33.3333 1392 29.1667L1440 25V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0V50Z"
            fill="currentColor"
            className="text-white/5"
          />
        </svg>
      </motion.div>

      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Logo and Description */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="col-span-1 space-y-6"
          >
            <Link to="/">
              <img
                src={profaLogo}
                alt="Profa Logo"
                width="150"
                height="94"
                loading="lazy"
                className="h-[5.85rem] w-auto object-contain hover:opacity-90 transition-all duration-200 active:scale-115 transform hover:scale-110 brightness-125 contrast-125 filter"
              />
            </Link>
            <p className="text-gray-400 text-lg leading-relaxed">
              Profesionalni i astrološki saveti za vaš lični i duhovni razvoj
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="col-span-1"
          >
            <h3 className="text-xl font-semibold text-white mb-6 bg-gradient-to-r from-[rgb(104,60,171)] to-[rgb(49,135,208)] bg-clip-text text-transparent">
              Brzi linkovi
            </h3>
            <ul className="space-y-4">
              <li>
                <Link
                  to="/o-meni/"
                  className="text-gray-400 hover:text-white text-lg transition-all duration-300 hover:translate-x-2 inline-block"
                >
                  O meni
                </Link>
              </li>
              <li>
                <Link
                  to="/astroloske-konsultacije/"
                  className="text-gray-400 hover:text-white text-lg transition-all duration-300 hover:translate-x-2 inline-block"
                >
                  Astrološke konsultacije
                </Link>
              </li>
              <li>
                <Link
                  to="/hipnoze/"
                  className="text-gray-400 hover:text-white text-lg transition-all duration-300 hover:translate-x-2 inline-block"
                >
                  Hipnoze
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="col-span-1"
          >
            <h3 className="text-xl font-semibold text-white mb-6 bg-gradient-to-r from-[rgb(104,60,171)] to-[rgb(49,135,208)] bg-clip-text text-transparent">
              Kontakt
            </h3>
            <ul className="space-y-4 mb-6">
              <li>
                <a
                  href="mailto:zoviprofu@gmail.com"
                  className="text-gray-400 hover:text-white text-lg transition-all duration-300 hover:translate-x-2 inline-block"
                >
                  Email: zoviprofu@gmail.com
                </a>
              </li>
            </ul>
            
            {/* Social Media Icons - Positioned under the email */}
            <div className="flex space-x-6 mt-2">
              <a
                href="https://www.facebook.com/MilosZivkovicProfa"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
                aria-label="Posetite Profin Facebook profil"
              >
                <div className="absolute -inset-2 bg-[#1877f2] rounded-full opacity-0 group-hover:opacity-20 transform group-hover:scale-110 transition-all duration-300"></div>
                <FaFacebook className="w-8 h-8 text-[#1877f2] transform group-hover:scale-110 transition-all duration-300" />
              </a>
              <a
                href="https://www.instagram.com/profa.official2/"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
                aria-label="Posetite Profin Instagram profil"
              >
                <div className="absolute -inset-2 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] rounded-full opacity-0 group-hover:opacity-20 transform group-hover:scale-110 transition-all duration-300"></div>
                <FaInstagram className="w-8 h-8 text-[#e4405f] transform group-hover:scale-110 transition-all duration-300" />
              </a>
              <a
                href="https://www.youtube.com/@ProfaPodcast"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
                aria-label="Posetite Profin YouTube kanal"
              >
                <div className="absolute -inset-2 bg-[#ff0000] rounded-full opacity-0 group-hover:opacity-20 transform group-hover:scale-110 transition-all duration-300"></div>
                <FaYoutube className="w-8 h-8 text-[#ff0000] transform group-hover:scale-110 transition-all duration-300" />
              </a>
              <a
                href="https://www.tiktok.com/@profa.official"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
                aria-label="Posetite Profin TikTok profil"
              >
                <div className="absolute -inset-2 bg-white rounded-full opacity-0 group-hover:opacity-20 transform group-hover:scale-110 transition-all duration-300"></div>
                <FaTiktok className="w-8 h-8 text-white transform group-hover:scale-110 transition-all duration-300" />
              </a>
            </div>
          </motion.div>
        </div>

        {/* Copyright with Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="border-t border-gray-800 pt-8"
        >
          <div className="flex flex-col md:flex-row items-center justify-center space-y-2 md:space-y-0 md:space-x-4">
            <p className="text-center text-gray-400">
              {currentYear} Miloš Živković - Profa <br />
              Sva prava zadržana.
            </p>
            <button
              onClick={handleDisclaimerToggle}
              className="group inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-all duration-300"
              aria-label="View disclaimer"
            >
              <span className="text-sm md:text-base font-medium">
                Pravno odricanje od odgovornosti - saglasni ste da prihvatate ove
                uslove na sopstvenu odgovornost:
              </span>
              <div className="relative w-8 h-8 group-hover:scale-105 transition-transform duration-300 active:scale-90 active:rotate-12 ml-2">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="w-8 h-8 relative z-10 transition-all duration-300 hover:rotate-6 drop-shadow-[0_2px_4px_rgba(167,139,250,0.5)]"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L2 22H22L12 2Z"
                    className="fill-[#FF00FF] stroke-none"
                    style={{ fill: "url(#gradient)" }}
                  />
                  <path
                    d="M12 16L12 8"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="19" r="1.25" fill="white" />
                  <defs>
                    <linearGradient id="gradient" x1="2" y1="2" x2="22" y2="22">
                      <stop offset="0%" stopColor="#A78BFA" />{" "}
                      {/* violet-400 */}
                      <stop offset="100%" stopColor="#60A5FA" />{" "}
                      {/* blue-400 */}
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Disclaimer Modal */}
      {isDisclaimerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleDisclaimerToggle}
          />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl px-4">
            <div
              className="bg-gray-900 rounded-xl w-full p-6 animate-modal-enter"
              onClick={(e) => e.stopPropagation()}
              style={{
                boxShadow: "0 0 40px rgba(104, 60, 171, 0.2)",
              }}
            >
              <button
                onClick={handleDisclaimerToggle}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-300"
                aria-label="Close disclaimer"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-[rgb(104,60,171)] to-[rgb(49,135,208)] bg-clip-text text-transparent">
                Dokumentacija o odgovornosti i zaštiti prava
              </h2>

              <div className="text-gray-300 space-y-4 max-h-[60vh] overflow-y-auto pr-4 custom-scrollbar">
                <p className="mb-4">
                  Preuzimanjem i korišćenjem MP3 datoteka hipnoze ili
                  pristupanjem astrološkim savetima sa ove veb stranice,
                  prihvatate sledeće uslove:
                </p>

                <div className="space-y-4 ml-4">
                  <p>
                    <span className="text-blue-400">*</span>Koristite na
                    sopstveni rizik: Dostavljeni sadržaj je samo u informativne
                    svrhe. Snimci hipnoze nisu namenjeni za dijagnostikovanje
                    ili lečenje bilo kog zdravstvenog stanja. Ako imate bilo
                    kakvih zdravstvenih problema, konsultujte se sa
                    kvalifikovanim zdravstvenim radnikom pre upotrebe ovih
                    snimaka.
                  </p>
                  <p>
                    <span className="text-blue-400">*</span>Astrološki saveti:
                    Astrološke informacije su zasnovane na opštim principima
                    astrologije i ne treba ih smatrati personalizovanim savetima
                    ili koristiti kao zamenu za profesionalne smernice u
                    oblastima kao što su finansije, zdravlje, odnosi itd.
                  </p>
                  <p>
                    <span className="text-blue-400">*</span>Bez garancije: Ne
                    dajemo garancije u pogledu efikasnosti ovih materijala ili
                    njihove podobnosti za vaše specifične potrebe.
                  </p>
                  <p>
                    <span className="text-blue-400">*</span>Ograničenje
                    odgovornosti: Razumete da se ne možemo smatrati odgovornim
                    za bilo kakvu štetu nastalu korišćenjem našeg sadržaja.
                  </p>
                  <p>
                    <span className="text-blue-400">*</span>Obaveštenje o
                    autorskim pravima: Svi materijali su zaštićeni autorskim
                    pravima od strane Miloša Živkovića - Profe. Neovlašćena
                    distribucija ili reprodukcija je strogo zabranjena.
                  </p>
                  <p>
                    <span className="text-blue-400">*</span>Starosno
                    ograničenje: Ovi resursi su namenjeni pojedincima starijim
                    od 18 godina, osim ako nije drugačije naznačeno.
                  </p>
                  <p>
                    <span className="text-blue-400">*</span>Odgovornost: Vaša je
                    odgovornost da osigurate da se pridržavate svih važećih
                    zakona kada pristupate ovom sadržaju iz vaše jurisdikcije.
                  </p>
                  <p>
                    <span className="text-blue-400">*</span>Promene Uslova:
                    Zadržavamo pravo da izmenimo ove uslove u bilo kom trenutku
                    bez obaveštenja.
                  </p>
                </div>

                <p className="mt-6 text-sm text-gray-400">
                  Ako nastavite sa preuzimanjem naših MP3 datoteka ili pristupom
                  astrološkim savetima, potvrđujete da ste pročitali i da se
                  slažete da ćete se pridržavati ovih uslova.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </footer>
  );
};

export default Footer;
